/**
 * Patient care page content. Carries the structural data (PDF hrefs and the
 * product / sub-label translation keys) that components combine with the
 * translated copy in `/src/locales/*` at render time. Documents are mirrored
 * locally under `/public/assets/patient-care/` so the buttons function
 * offline and the URLs survive any future change to the public clearlab.com
 * paths.
 */

export interface InstructionDoc {
  /** Translation key under `patientCare.ifu.languages` is not used; the
   *  language list itself is data, not translatable copy — region-specific
   *  PDFs bundle their own languages. */
  languages: string;
  href: string;
}

export interface ProductDocument {
  /** Lookup key under `products.items.<productKey>.name`. */
  productKey: string;
  /** Optional lookup key under `patientCare.subLabels.<subLabelKey>`. */
  subLabelKey?: 'includesToric' | 'includesPhantom' | 'includesPhantomAndOptimore';
  href: string;
}

export const INSTRUCTION_DOCS: readonly InstructionDoc[] = [
  {
    languages:
      'Danish, Dutch, English, French, German, Greek, Italian, Portuguese, Norwegian, Spanish, Swedish',
    href: '/assets/patient-care/ifu-001-western-european.pdf',
  },
  {
    languages:
      'Czech, Estonian, Finnish, Hungarian, Latvian, Lithuanian, Maltese, Polish, Slovenian, Slovakian',
    href: '/assets/patient-care/ifu-002-central-eastern-european.pdf',
  },
];

export const PACKAGE_INSERT_ITEMS: readonly ProductDocument[] = [
  {
    productKey: 'clear-1-day',
    subLabelKey: 'includesToric',
    href: '/assets/patient-care/clear-1-day-package-insert.pdf',
  },
  {
    productKey: 'clear-all-day',
    subLabelKey: 'includesToric',
    href: '/assets/patient-care/clear-all-day-package-insert.pdf',
  },
  { productKey: 'clear-38', href: '/assets/patient-care/clear-38-package-insert.pdf' },
  { productKey: 'clear-55a', href: '/assets/patient-care/clear-55-package-insert.pdf' },
  { productKey: 'clear-55a-uv', href: '/assets/patient-care/clear-55-uv-package-insert.pdf' },
  { productKey: 'clear-58', href: '/assets/patient-care/clear-58-package-insert.pdf' },
  {
    productKey: 'clearcolor-1-day',
    subLabelKey: 'includesPhantom',
    href: '/assets/patient-care/clearcolor-1-day-package-insert.pdf',
  },
  {
    productKey: 'clearcolor',
    subLabelKey: 'includesPhantomAndOptimore',
    href: '/assets/patient-care/package-insert-clearcolor-phantom-Optimore_(EN,DE,FR,IT,ES,PL,NL,PT,EL).pdf',
  },
  { productKey: 'eyedia', href: '/assets/patient-care/eyedia-package-insert.pdf' },
];

export const FITTING_GUIDE_ITEMS: readonly ProductDocument[] = [
  {
    productKey: 'clear-1-day',
    subLabelKey: 'includesToric',
    href: '/assets/patient-care/clear-1-day-fitting-guide.pdf',
  },
  {
    productKey: 'clear-all-day',
    subLabelKey: 'includesToric',
    href: '/assets/patient-care/clear-all-day-fitting-guide.pdf',
  },
  { productKey: 'clear-38', href: '/assets/patient-care/clear-38-fitting-guide.pdf' },
  { productKey: 'clear-55a', href: '/assets/patient-care/clear-55-fitting-guide.pdf' },
  { productKey: 'clear-55a-uv', href: '/assets/patient-care/clear-55-uv-fitting-guide.pdf' },
  { productKey: 'clear-58', href: '/assets/patient-care/clear-58-fitting-guide.pdf' },
  {
    productKey: 'clearcolor-1-day',
    subLabelKey: 'includesPhantom',
    href: '/assets/patient-care/clearcolor-1-day-fitting-guide.pdf',
  },
  {
    productKey: 'clearcolor',
    subLabelKey: 'includesPhantomAndOptimore',
    href: '/assets/patient-care/professional-fitting-guide_clearcolor-clearcolor-phantom-optimore.pdf',
  },
];

export const IFU_SECTION_ID = 'patient-care-ifu-heading';
export const PACKAGE_INSERTS_SECTION_ID = 'patient-care-package-inserts-heading';
export const FITTING_GUIDES_SECTION_ID = 'patient-care-fitting-guides-heading';
